import React, {
  useState,
  createContext,
  useContext,
  useMemo,
  useEffect,
} from "react";
import {
  Routes,
  Route,
  useNavigate,
  useLocation,
  Navigate,
} from "react-router-dom";
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  Drawer,
  IconButton,
  CssBaseline,
  TextField,
  InputAdornment,
  Avatar,
  Button,
  Menu,
  MenuItem,
  Paper,
  ListItemIcon,
  ListItemText,
  Chip,
  Stack,
  ClickAwayListener,
} from "@mui/material";
import {
  Menu as MenuIcon,
  Search as SearchIcon,
  Logout,
  History,
  Clear,
  Apps,
  ViewInAr,
  Business,
  NavigateNext,
  FilterList,
} from "@mui/icons-material";
import "./App.css";

// Import components and pages
import { Navigation, LoadingSpinner } from "./components";
import DashboardPage from "./pages/Dashboard";
import InventoryPage from "./pages/Inventory";
import LoginPage from "./pages/Login";
import AuthCallback from "./pages/AuthCallback";
import RequestImprovementPage from "./pages/RequestImprovement";
import MerchantsPage from "./pages/Merchants";
import CategoriesPage from "./pages/Categories";
import SettingsPage from "./pages/Settings";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import { useQuery } from "@tanstack/react-query";
import { supabaseApi } from "./services/supabaseApi";
import { useDebounce } from "./hooks/useDebounce";

// Global Search Types
interface SearchResult {
  id: string;
  title: string;
  description?: string;
  type: "item" | "category" | "merchant" | "filter";
  path: string;
  score: number;
  highlighted: string;
  metadata?: {
    price?: number;
    status?: string;
    count?: number;
    website?: string;
    filterType?: "merchant" | "category";
    filterId?: string;
    filterName?: string;
  };
  merchant?: {
    id: string;
    name: string;
    image_url?: string;
  };
}

interface SearchContextType {
  searchValue: string;
  setSearchValue: (value: string) => void;
  searchResults: SearchResult[];
  isSearching: boolean;
  showSearchResults: boolean;
  setShowSearchResults: (show: boolean) => void;
  handleResultClick: (result: SearchResult) => void;
  activeFilters: Array<{
    id: string;
    type: "merchant" | "category";
    name: string;
  }>;
  addFilter: (filter: {
    id: string;
    type: "merchant" | "category";
    name: string;
  }) => void;
  removeFilter: (filterId: string) => void;
  clearAllFilters: () => void;
}

const SearchContext = createContext<SearchContextType | undefined>(undefined);

export const useSearch = () => {
  const context = useContext(SearchContext);
  if (!context) {
    throw new Error("useSearch must be used within a SearchProvider");
  }
  return context;
};

// Search utilities
const GLOBAL_SEARCH_HISTORY_KEY = "global_search_history";
const MAX_HISTORY_ITEMS = 3;

const getGlobalSearchHistory = (): string[] => {
  try {
    const history = localStorage.getItem(GLOBAL_SEARCH_HISTORY_KEY);
    return history ? JSON.parse(history) : [];
  } catch {
    return [];
  }
};

const addToGlobalSearchHistory = (searchTerm: string) => {
  if (!searchTerm.trim()) return;

  try {
    const history = getGlobalSearchHistory();
    const filtered = history.filter((item) => item !== searchTerm);
    const newHistory = [searchTerm, ...filtered].slice(0, MAX_HISTORY_ITEMS);
    localStorage.setItem(GLOBAL_SEARCH_HISTORY_KEY, JSON.stringify(newHistory));
  } catch {
    // Ignore localStorage errors
  }
};

const highlightText = (text: string, searchTerm: string): string => {
  if (!searchTerm.trim()) return text;

  const regex = new RegExp(
    `(${searchTerm.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`,
    "gi"
  );
  return text.replace(
    regex,
    '<mark style="background-color: #fff3cd; padding: 1px 2px; border-radius: 2px;">$1</mark>'
  );
};

// Global fuzzy search function
const performGlobalSearch = (
  searchTerm: string,
  items: any[],
  categories: any[],
  merchants: any[]
): SearchResult[] => {
  if (!searchTerm.trim()) return [];

  const term = searchTerm.toLowerCase().trim();
  const results: SearchResult[] = [];

  // Search in items
  items?.forEach((item) => {
    let score = 0;
    const nameMatch = item.name.toLowerCase();
    const descMatch = item.description?.toLowerCase() || "";

    if (nameMatch.includes(term)) {
      const exactMatch = nameMatch === term;
      const startsWith = nameMatch.startsWith(term);
      score += exactMatch ? 100 : startsWith ? 80 : 60;
    }

    if (descMatch.includes(term)) {
      score += 30;
    }

    // Partial word matching
    const words = term.split(" ").filter((w) => w.length > 1);
    words.forEach((word) => {
      if (nameMatch.includes(word) && !nameMatch.includes(term)) {
        score += 15;
      }
    });

    if (score > 0) {
      results.push({
        id: item.id,
        title: item.name,
        description: item.description,
        type: "item",
        path: "/inventory",
        score,
        highlighted: highlightText(item.name, term),
        metadata: {
          price: item.price,
          status: item.status,
        },
      });
    }
  });

  // Search in categories
  categories?.forEach((category) => {
    let score = 0;
    const nameMatch = category.name.toLowerCase();
    const descMatch = category.description?.toLowerCase() || "";

    if (nameMatch.includes(term)) {
      const exactMatch = nameMatch === term;
      const startsWith = nameMatch.startsWith(term);
      score += exactMatch ? 100 : startsWith ? 80 : 60;
    }

    if (descMatch.includes(term)) {
      score += 30;
    }

    if (score > 0) {
      // Add the main category result
      results.push({
        id: category.id,
        title: category.name,
        description: category.description,
        type: "category",
        path: "/categories",
        score,
        highlighted: highlightText(category.name, term),
        metadata: {
          count: category.item_count || 0,
        },
      });

      // Add inventory filter option for this category
      results.push({
        id: `filter-category-${category.id}`,
        title: `Filter inventory by ${category.name}`,
        description: "View all items in this category in inventory",
        type: "filter",
        path: "/inventory",
        score: score - 5, // Slightly lower score than the main category result
        highlighted: `Filter inventory by ${highlightText(category.name, term)}`,
        metadata: {
          filterType: "category",
          filterId: category.id,
          filterName: category.name,
        },
      });
    }
  });

  // Search in merchants
  merchants?.forEach((merchant) => {
    let score = 0;
    const nameMatch = merchant.name.toLowerCase();
    const urlMatch = merchant.url?.toLowerCase() || "";
    const websiteMatch = merchant.website?.toLowerCase() || "";

    if (nameMatch.includes(term)) {
      const exactMatch = nameMatch === term;
      const startsWith = nameMatch.startsWith(term);
      score += exactMatch ? 100 : startsWith ? 80 : 60;
    }

    if (urlMatch.includes(term)) {
      score += 40;
    }

    if (websiteMatch.includes(term)) {
      score += 35;
    }

    if (score > 0) {
      // Add the main merchant result
      results.push({
        id: merchant.id,
        title: merchant.name,
        description: merchant.website,
        type: "merchant",
        path: "/merchants",
        score,
        highlighted: highlightText(merchant.name, term),
        metadata: {
          website: merchant.website,
        },
        merchant: {
          id: merchant.id,
          name: merchant.name,
          image_url: merchant.image_url,
        },
      });

      // Add inventory filter option for this merchant
      results.push({
        id: `filter-merchant-${merchant.id}`,
        title: `Filter inventory by ${merchant.name}`,
        description: "View all items from this merchant in inventory",
        type: "filter",
        path: "/inventory",
        score: score - 5, // Slightly lower score than the main merchant result
        highlighted: `Filter inventory by ${highlightText(merchant.name, term)}`,
        metadata: {
          filterType: "merchant",
          filterId: merchant.id,
          filterName: merchant.name,
        },
        merchant: {
          id: merchant.id,
          name: merchant.name,
          image_url: merchant.image_url,
        },
      });
    }
  });

  // Sort by score and limit results
  return results.sort((a, b) => b.score - a.score).slice(0, 20);
};

// Enhanced Search Component
interface EnhancedSearchProps {
  searchValue: string;
  setSearchValue: (value: string) => void;
  searchResults: SearchResult[];
  isSearching: boolean;
  showSearchResults: boolean;
  setShowSearchResults: (show: boolean) => void;
  onResultClick: (result: SearchResult) => void;
  activeFilters: Array<{
    id: string;
    type: "merchant" | "category";
    name: string;
  }>;
  onRemoveFilter: (filterId: string) => void;
}

const EnhancedSearch: React.FC<EnhancedSearchProps> = ({
  searchValue,
  setSearchValue,
  searchResults,
  isSearching,
  showSearchResults,
  setShowSearchResults,
  onResultClick,
  activeFilters,
  onRemoveFilter,
}) => {
  const [searchHistory, setSearchHistory] = useState<string[]>([]);

  useEffect(() => {
    setSearchHistory(getGlobalSearchHistory());
  }, []);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearchValue(value);
    setShowSearchResults(true);
  };

  const handleInputFocus = (event: React.FocusEvent<HTMLInputElement>) => {
    setShowSearchResults(true);
  };

  const handleClear = () => {
    setSearchValue("");
    setShowSearchResults(false);
  };

  const handleHistorySelect = (historyItem: string) => {
    setSearchValue(historyItem);
    setShowSearchResults(true);
  };

  const handleResultSelect = (result: SearchResult) => {
    addToGlobalSearchHistory(searchValue);
    setSearchHistory(getGlobalSearchHistory());
    onResultClick(result);
    setShowSearchResults(false);
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case "item":
        return <ViewInAr sx={{ fontSize: 18 }} />;
      case "category":
        return <Apps sx={{ fontSize: 18 }} />;
      case "merchant":
        return <Business sx={{ fontSize: 18 }} />;
      case "filter":
        return <FilterList sx={{ fontSize: 18 }} />;
      default:
        return <SearchIcon sx={{ fontSize: 18 }} />;
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case "item":
        return "#2e7d32";
      case "category":
        return "#ed6c02";
      case "merchant":
        return "#1565c0";
      case "filter":
        return "#7b1fa2";
      default:
        return "#666";
    }
  };

  const hasContent = searchValue.trim() || searchHistory.length > 0;

  return (
    <ClickAwayListener onClickAway={() => setShowSearchResults(false)}>
      <Box sx={{ position: "relative", flexGrow: 1, mx: 0 }}>
        <TextField
          placeholder="Search items, categories, merchants..."
          value={searchValue}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <SearchIcon sx={{ color: "action.active" }} />
                  {/* Filter Badges */}
                  {activeFilters.map((filter) => (
                    <Chip
                      key={`${filter.type}-${filter.id}`}
                      label={filter.name}
                      size="small"
                      variant="outlined"
                      onDelete={() => onRemoveFilter(filter.id)}
                      deleteIcon={
                        <Clear
                          sx={{
                            fontSize: 14,
                            "&:hover": {
                              color: "error.main",
                            },
                          }}
                        />
                      }
                      sx={{
                        height: 24,
                        fontSize: "0.75rem",
                        backgroundColor:
                          filter.type === "merchant"
                            ? "rgba(21, 101, 192, 0.08)"
                            : "rgba(237, 108, 2, 0.08)",
                        borderColor:
                          filter.type === "merchant"
                            ? "rgba(21, 101, 192, 0.3)"
                            : "rgba(237, 108, 2, 0.3)",
                        color:
                          filter.type === "merchant" ? "#1565c0" : "#ed6c02",
                        "& .MuiChip-deleteIcon": {
                          color:
                            filter.type === "merchant" ? "#1565c0" : "#ed6c02",
                        },
                        "&:hover": {
                          backgroundColor:
                            filter.type === "merchant"
                              ? "rgba(21, 101, 192, 0.12)"
                              : "rgba(237, 108, 2, 0.12)",
                        },
                      }}
                    />
                  ))}
                </Stack>
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <Stack direction="row" spacing={0.5} alignItems="center">
                  {isSearching && (
                    <Box
                      sx={{
                        width: 16,
                        height: 16,
                        border: "2px solid",
                        borderColor: "primary.main",
                        borderTopColor: "transparent",
                        borderRadius: "50%",
                        animation: "spin 1s linear infinite",
                        "@keyframes spin": {
                          "0%": { transform: "rotate(0deg)" },
                          "100%": { transform: "rotate(360deg)" },
                        },
                      }}
                    />
                  )}
                  {searchValue && (
                    <IconButton
                      size="small"
                      onClick={handleClear}
                      sx={{ opacity: 0.7, "&:hover": { opacity: 1 } }}
                    >
                      <Clear sx={{ fontSize: 16 }} />
                    </IconButton>
                  )}
                  <Chip
                    size="small"
                    label="⌘K"
                    sx={{
                      height: 20,
                      fontSize: "0.7rem",
                      backgroundColor: (theme) =>
                        theme.palette.mode === "dark"
                          ? "rgba(255, 255, 255, 0.1)"
                          : "rgba(0,0,0,0.05)",
                      color: "text.secondary",
                    }}
                  />
                </Stack>
              </InputAdornment>
            ),
          }}
          sx={{
            width: "100%",
            "& .MuiOutlinedInput-root": {
              height: 40,
              borderRadius: 2,
              backgroundColor: (theme) =>
                theme.palette.mode === "dark"
                  ? "rgba(255, 255, 255, 0.08)"
                  : "rgba(255, 255, 255, 0.95)",
              border: (theme) =>
                theme.palette.mode === "dark"
                  ? "1px solid rgba(255, 255, 255, 0.12)"
                  : "1px solid rgba(0, 0, 0, 0.1)",
              "& fieldset": {
                border: "none",
              },
              "&:hover": {
                backgroundColor: (theme) =>
                  theme.palette.mode === "dark"
                    ? "rgba(255, 255, 255, 0.12)"
                    : "rgba(255, 255, 255, 1)",
                border: (theme) =>
                  theme.palette.mode === "dark"
                    ? "1px solid rgba(255, 255, 255, 0.16)"
                    : "1px solid rgba(0, 0, 0, 0.15)",
              },
              "&.Mui-focused": {
                backgroundColor: (theme) =>
                  theme.palette.mode === "dark"
                    ? "rgba(255, 255, 255, 0.12)"
                    : "rgba(255, 255, 255, 1)",
                border: "2px solid",
                borderColor: "primary.main",
              },
            },
          }}
          variant="outlined"
        />

        {/* Search Results Dropdown */}
        {showSearchResults && hasContent && (
          <Paper
            sx={{
              position: "absolute",
              top: "100%",
              left: 0,
              right: 0,
              mt: 1,
              maxHeight: 400,
              overflow: "auto",
              zIndex: 1300,
              borderRadius: 2,
              boxShadow: "0 8px 32px rgba(0, 0, 0, 0.12)",
              border: "1px solid rgba(0, 0, 0, 0.1)",
            }}
          >
            {/* Search Results */}
            {searchValue.trim() && (
              <>
                <Box sx={{ p: 2, pb: 1 }}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <SearchIcon
                      sx={{ fontSize: 16, color: "text.secondary" }}
                    />
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      fontWeight={600}
                    >
                      {searchResults.length} result
                      {searchResults.length !== 1 ? "s" : ""} for "{searchValue}
                      "
                    </Typography>
                  </Stack>
                </Box>

                {searchResults.length > 0 ? (
                  searchResults.map((result, index) => (
                    <MenuItem
                      key={`${result.type}-${result.id}`}
                      onClick={() => handleResultSelect(result)}
                      sx={{
                        py: 1.5,
                        px: 2,
                        alignItems: "flex-start",
                        "&:hover": {
                          backgroundColor: (theme) =>
                            theme.palette.mode === "dark"
                              ? "rgba(255, 255, 255, 0.08)"
                              : "rgba(0, 0, 0, 0.04)",
                        },
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 36, mt: 0.5 }}>
                        {(result.type === "merchant" ||
                          (result.type === "filter" &&
                            result.metadata?.filterType === "merchant")) &&
                        result.merchant ? (
                          <Avatar
                            src={result.merchant.image_url}
                            sx={{
                              width: 28,
                              height: 28,
                              backgroundColor: getTypeColor(result.type),
                              fontSize: "0.75rem",
                            }}
                          >
                            {result.merchant.name[0]}
                          </Avatar>
                        ) : (
                          <Box
                            sx={{
                              color: getTypeColor(result.type),
                              backgroundColor: `${getTypeColor(result.type)}15`,
                              borderRadius: 1,
                              p: 0.5,
                              display: "flex",
                            }}
                          >
                            {getTypeIcon(result.type)}
                          </Box>
                        )}
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Typography
                            variant="body2"
                            fontWeight={500}
                            dangerouslySetInnerHTML={{
                              __html: result.highlighted,
                            }}
                          />
                        }
                        secondary={
                          <Stack spacing={0.5}>
                            {result.description && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                noWrap
                              >
                                {result.description}
                              </Typography>
                            )}
                            <Stack
                              direction="row"
                              spacing={1}
                              alignItems="center"
                            >
                              <Chip
                                size="small"
                                label={result.type}
                                sx={{
                                  height: 16,
                                  fontSize: "0.65rem",
                                  backgroundColor: `${getTypeColor(result.type)}15`,
                                  color: getTypeColor(result.type),
                                  "& .MuiChip-label": { px: 1 },
                                }}
                              />
                              {result.metadata?.price && (
                                <Typography
                                  variant="caption"
                                  color="success.main"
                                  fontWeight={600}
                                >
                                  ${result.metadata.price}
                                </Typography>
                              )}
                              {result.metadata?.count !== undefined && (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  {result.metadata.count} items
                                </Typography>
                              )}
                            </Stack>
                          </Stack>
                        }
                      />
                      <NavigateNext
                        sx={{ fontSize: 16, color: "text.disabled", ml: 1 }}
                      />
                    </MenuItem>
                  ))
                ) : (
                  <Box sx={{ p: 3, textAlign: "center" }}>
                    <SearchIcon
                      sx={{ fontSize: 32, color: "text.disabled", mb: 1 }}
                    />
                    <Typography variant="body2" color="text.secondary">
                      No results found for "{searchValue}"
                    </Typography>
                  </Box>
                )}
              </>
            )}

            {/* Search History */}
            {!searchValue.trim() && searchHistory.length > 0 && (
              <>
                <Box sx={{ p: 2, pb: 1 }}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <History sx={{ fontSize: 16, color: "text.secondary" }} />
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      fontWeight={600}
                    >
                      Recent searches
                    </Typography>
                  </Stack>
                </Box>
                {searchHistory.map((historyItem, index) => (
                  <MenuItem
                    key={index}
                    onClick={() => handleHistorySelect(historyItem)}
                    sx={{
                      py: 1,
                      px: 2,
                      "&:hover": {
                        backgroundColor: (theme) =>
                          theme.palette.mode === "dark"
                            ? "rgba(255, 255, 255, 0.08)"
                            : "rgba(0, 0, 0, 0.04)",
                      },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <History sx={{ fontSize: 16, color: "text.secondary" }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={historyItem}
                      primaryTypographyProps={{
                        variant: "body2",
                        color: "text.primary",
                      }}
                    />
                  </MenuItem>
                ))}
              </>
            )}

            {/* Empty state */}
            {!searchValue.trim() && searchHistory.length === 0 && (
              <Box sx={{ p: 3, textAlign: "center" }}>
                <SearchIcon
                  sx={{ fontSize: 32, color: "text.disabled", mb: 1 }}
                />
                <Typography variant="body2" color="text.secondary">
                  Start typing to search across your inventory
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mt: 1, display: "block" }}
                >
                  Press ⌘K to focus search
                </Typography>
              </Box>
            )}
          </Paper>
        )}
      </Box>
    </ClickAwayListener>
  );
};

// Protected Route Component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return <LoadingSpinner fullHeight message="Setting up your workspace..." />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return <>{children}</>;
};

// App Layout Component (for authenticated users)
const AppLayout: React.FC = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [activeFilters, setActiveFilters] = useState<
    Array<{
      id: string;
      type: "merchant" | "category";
      name: string;
    }>
  >([]);
  const { user, signOut } = useAuth();
  const navigate = useNavigate();

  const drawerWidth = 80;
  const debouncedSearchValue = useDebounce(searchValue, 300);

  // Fetch data for global search
  const { data: items = [] } = useQuery({
    queryKey: ["items"],
    queryFn: supabaseApi.items.getAll,
  });

  const { data: categories = [] } = useQuery({
    queryKey: ["categories"],
    queryFn: supabaseApi.categories.getAll,
  });

  const { data: merchants = [] } = useQuery({
    queryKey: ["merchants"],
    queryFn: supabaseApi.merchants.getAll,
  });

  // Perform global search
  const searchResults = useMemo(() => {
    return performGlobalSearch(
      debouncedSearchValue,
      items,
      categories,
      merchants
    );
  }, [debouncedSearchValue, items, categories, merchants]);

  const isSearching = searchValue !== debouncedSearchValue;

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleProfileClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileClose = () => {
    setAnchorEl(null);
  };

  const handleSignOut = async () => {
    await signOut();
    handleProfileClose();
    navigate("/login");
  };

  const addFilter = (filter: {
    id: string;
    type: "merchant" | "category";
    name: string;
  }) => {
    setActiveFilters((prevFilters) => {
      // Check if filter already exists
      if (
        prevFilters.some((f) => f.id === filter.id && f.type === filter.type)
      ) {
        return prevFilters;
      }
      return [...prevFilters, filter];
    });
  };

  const removeFilter = (filterId: string) => {
    setActiveFilters((prevFilters) =>
      prevFilters.filter((f) => f.id !== filterId)
    );
  };

  const clearAllFilters = () => {
    setActiveFilters([]);
  };

  const handleResultClick = (result: SearchResult) => {
    // Add search term to history
    addToGlobalSearchHistory(searchValue);

    // Handle different result types
    if (result.type === "item") {
      // Navigate to inventory with item ID parameter
      navigate(`/inventory?item=${result.id}`);
    } else if (result.type === "filter") {
      // Handle inventory filtering
      if (result.metadata?.filterType === "merchant") {
        // Add filter to active filters
        addFilter({
          id: result.metadata.filterId!,
          type: "merchant",
          name: result.metadata.filterName!,
        });
        navigate(`/inventory?merchant=${result.metadata.filterId}`);
      } else if (result.metadata?.filterType === "category") {
        // Add filter to active filters
        addFilter({
          id: result.metadata.filterId!,
          type: "category",
          name: result.metadata.filterName!,
        });
        navigate(`/inventory?category=${result.metadata.filterId}`);
      }
    } else {
      // Navigate to the page for other types
      navigate(result.path);
    }

    setSearchValue("");
    setShowSearchResults(false);
  };

  // Keyboard shortcuts for search
  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Focus search on Ctrl/Cmd + K
      if ((event.ctrlKey || event.metaKey) && event.key === "k") {
        event.preventDefault();
        const searchInput = document.querySelector(
          'input[placeholder*="Search"]'
        ) as HTMLInputElement;
        if (searchInput) {
          searchInput.focus();
          setShowSearchResults(true);
        }
      }
      // Clear search on Escape
      if (event.key === "Escape") {
        setSearchValue("");
        setShowSearchResults(false);
        const searchInput = document.querySelector(
          'input[placeholder*="Search"]'
        ) as HTMLInputElement;
        if (searchInput) {
          searchInput.blur();
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);

  const drawer = (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <Navigation onNavigate={() => setMobileOpen(false)} />
    </Box>
  );

  const searchContextValue: SearchContextType = {
    searchValue,
    setSearchValue,
    searchResults,
    isSearching,
    showSearchResults,
    setShowSearchResults,
    handleResultClick,
    activeFilters,
    addFilter,
    removeFilter,
    clearAllFilters,
  };

  return (
    <SearchContext.Provider value={searchContextValue}>
      <CssBaseline />
      <style>
        {`
          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.7; }
          }
        `}
      </style>
      <Box
        sx={{
          display: "flex",
          minHeight: "100vh",
          backgroundColor: "background.default",
          backgroundImage: (theme) =>
            theme.palette.mode === "dark"
              ? "linear-gradient(135deg, #0D1117 0%, #1C2128 100%)"
              : "linear-gradient(135deg, #F8F9FA 0%, #E3F2FD 100%)",
          backgroundAttachment: "fixed",
        }}
      >
        {/* Header with Enhanced Search Bar */}
        <AppBar
          position="fixed"
          elevation={0}
          sx={{
            width: { md: `calc(100% - ${drawerWidth}px)` },
            ml: { md: `${drawerWidth}px` },
            backgroundColor: "transparent",
            backdropFilter: "none",
            borderBottom: "none",
            boxShadow: "none",
            zIndex: 1200,
          }}
        >
          <Toolbar
            sx={{
              height: { xs: 48, sm: 56, md: 64 }, // More compact on mobile
              px: { xs: 1.5, sm: 3, md: 4 }, // Reduced horizontal padding on mobile
            }}
          >
            {/* Mobile menu button */}
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{
                mr: { xs: 1.5, sm: 2 }, // Reduced margin on mobile
                display: { md: "none" },
                color: "text.primary",
                padding: { xs: 1, sm: 1.5 }, // Reduced padding on mobile
              }}
            >
              <MenuIcon />
            </IconButton>

            {/* Enhanced Search Bar */}
            <EnhancedSearch
              searchValue={searchValue}
              setSearchValue={setSearchValue}
              searchResults={searchResults}
              isSearching={isSearching}
              showSearchResults={showSearchResults}
              setShowSearchResults={setShowSearchResults}
              onResultClick={handleResultClick}
              activeFilters={activeFilters}
              onRemoveFilter={removeFilter}
            />

            {/* Profile Menu */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: { xs: 1, sm: 2 }, // Reduced gap on mobile
                ml: { xs: 2, sm: 3 }, // Reduced margin on mobile
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  display: { xs: "none", sm: "block" },
                  color: "text.primary",
                  fontWeight: 500,
                }}
              >
                {user?.name || user?.email?.split("@")[0]}
              </Typography>
              <Button
                onClick={handleProfileClick}
                sx={{
                  minWidth: "auto",
                  borderRadius: 2,
                  p: 1,
                  color: "text.primary",
                  "&:hover": {
                    backgroundColor: "action.hover",
                  },
                }}
              >
                <Avatar
                  sx={{
                    width: { xs: 28, sm: 32 }, // Smaller on mobile
                    height: { xs: 28, sm: 32 }, // Smaller on mobile
                    fontSize: { xs: "0.875rem", sm: "1rem" }, // Smaller text on mobile
                    backgroundColor: "primary.main",
                  }}
                >
                  {user?.name?.[0] || user?.email?.[0] || "U"}
                </Avatar>
              </Button>
              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleProfileClose}
                transformOrigin={{ horizontal: "right", vertical: "top" }}
                anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
                sx={{
                  "& .MuiPaper-root": {
                    borderRadius: 2,
                    minWidth: 180,
                    boxShadow: (theme) =>
                      theme.palette.mode === "dark"
                        ? "0 8px 32px rgba(0, 0, 0, 0.6)"
                        : "0 8px 32px rgba(0, 0, 0, 0.12)",
                  },
                }}
              >
                <MenuItem onClick={handleSignOut} sx={{ color: "error.main" }}>
                  <Logout sx={{ mr: 2, fontSize: "1.2rem" }} />
                  Sign Out
                </MenuItem>
              </Menu>
            </Box>
          </Toolbar>
        </AppBar>

        {/* Enhanced Sidebar Navigation */}
        <Box
          component="nav"
          sx={{
            width: { md: drawerWidth },
            flexShrink: { md: 0 },
          }}
        >
          {/* Mobile drawer */}
          <Drawer
            variant="temporary"
            open={mobileOpen}
            onClose={handleDrawerToggle}
            ModalProps={{
              keepMounted: true,
            }}
            sx={{
              display: { xs: "block", md: "none" },
              "& .MuiDrawer-paper": {
                boxSizing: "border-box",
                width: 260, // Reduced from 280 for more compact mobile navbar
                background: (theme) =>
                  theme.palette.mode === "dark"
                    ? "linear-gradient(135deg, rgba(22, 27, 34, 0.95) 0%, rgba(30, 36, 47, 0.95) 100%)"
                    : "linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.95) 100%)",
                backdropFilter: "blur(20px)",
                borderRight: (theme) =>
                  theme.palette.mode === "dark"
                    ? "1px solid rgba(240, 246, 252, 0.1)"
                    : "1px solid rgba(0, 0, 0, 0.08)",
                boxShadow: (theme) =>
                  theme.palette.mode === "dark"
                    ? "4px 0 20px rgba(0, 0, 0, 0.4)"
                    : "4px 0 20px rgba(0, 0, 0, 0.1)",
                borderRadius: "0 8px 8px 0", // Only round the right side
              },
            }}
          >
            {drawer}
          </Drawer>

          {/* Desktop drawer */}
          <Drawer
            variant="permanent"
            sx={{
              display: { xs: "none", md: "block" },
              "& .MuiDrawer-paper": {
                boxSizing: "border-box",
                width: drawerWidth,
                background: "transparent",
                borderRight: "none",
                boxShadow: "none",
                overflow: "visible",
              },
            }}
            open
          >
            {drawer}
          </Drawer>
        </Box>

        {/* Main content */}
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            width: { md: `calc(100% - ${drawerWidth}px)` },
            pt: { xs: 8, md: 9 },
            px: { xs: 0, md: 3 }, // Remove horizontal padding on mobile
            pb: 3,
          }}
        >
          <Routes>
            <Route path="/" element={<DashboardPage />} />
            <Route path="/inventory" element={<InventoryPage />} />
            <Route path="/categories" element={<CategoriesPage />} />
            <Route path="/merchants" element={<MerchantsPage />} />
            <Route
              path="/request-improvement"
              element={<RequestImprovementPage />}
            />
            <Route path="/settings" element={<SettingsPage />} />
          </Routes>
        </Box>
      </Box>
    </SearchContext.Provider>
  );
};

function App() {
  return (
    <AuthProvider>
      <ThemeProvider>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route
            path="/*"
            element={
              <ProtectedRoute>
                <AppLayout />
              </ProtectedRoute>
            }
          />
        </Routes>
      </ThemeProvider>
    </AuthProvider>
  );
}

export default App;
